import { useEffect, useState, useRef, ComponentType } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, BellIcon, MapIcon, CheckIcon, Building2Icon, SunIcon, MoonIcon, MonitorIcon } from 'lucide-react';
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: ComponentType<{
    className?: string;
  }>;
}
interface MobileHeaderProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  onOpenMap: () => void;
  onOpenAlarms: () => void;
  hasAlarms?: boolean;
  onBack?: () => void;
  currentRoomName?: string;
  sensorsCount?: number;
  apartmentLabel?: string;
  themePref?: 'dark' | 'light' | 'system';
  onThemeToggle?: () => void;
}
export function MobileHeader({
  rooms,
  selectedRoomId,
  onSelectRoom,
  onOpenMap,
  onOpenAlarms,
  hasAlarms = false,
  onBack,
  apartmentLabel,
  themePref = 'system',
  onThemeToggle,
}: MobileHeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const currentRoom = rooms.find((r) => r.id === selectedRoomId) || rooms[0];
  if (!currentRoom) return null;
  const handleSelectRoom = (id: string) => {
    onSelectRoom(id);
    setIsDropdownOpen(false);
  };
  return (
    <div className="relative z-30">
      {/* Header Bar */}
      <div
        className="h-14 flex items-center justify-between px-4 backdrop-blur-md border-b"
        style={{ backgroundColor: 'var(--mobile-bg-header)', borderColor: 'var(--mobile-border)' }}
      >
        {/* Left: Zone Dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border active:opacity-80 transition-colors min-w-0"
            style={{ backgroundColor: 'var(--mobile-bg-sub)', borderColor: 'var(--mobile-border)', color: 'var(--mobile-text-secondary)' }}
          >
            {(() => {
              const RoomIcon = currentRoom.icon;
              return <RoomIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />;
            })()}
            <span className="text-xs font-medium truncate max-w-[80px]" style={{ color: 'var(--mobile-text-secondary)' }}>
              {currentRoom.name}
            </span>
            <ChevronDownIcon
              className={`w-3.5 h-3.5 text-slate-500 flex-shrink-0 transition-transform duration-200 ${
                isDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 mt-1.5 w-48 py-1.5 rounded-xl backdrop-blur-xl border shadow-2xl shadow-black/50 overflow-hidden max-h-[60vh] overflow-y-auto"
                style={{ backgroundColor: 'var(--mobile-bg-dropdown)', borderColor: 'var(--mobile-border-strong)' }}
              >
                {rooms.map((room) => {
                  const isSelected = room.id === selectedRoomId;
                  const RoomIcon = room.icon;
                  const isAll = room.id === 'ALL';
                  return (
                    <button
                      key={room.id}
                      onClick={() => handleSelectRoom(room.id)}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 transition-colors ${
                        isSelected ? 'bg-cyan-500/15 text-cyan-300' : 'active:opacity-60'
                      } ${!isAll ? 'pl-8' : ''}`}
                      style={isSelected ? {} : { color: 'var(--mobile-text-secondary)' }}
                    >
                      <RoomIcon
                        className={`w-4 h-4 flex-shrink-0 ${
                          isSelected ? 'text-cyan-400' : 'text-[var(--mobile-text-muted)]'
                        }`}
                      />
                      <span className="text-sm font-medium flex-1 text-left">{room.name}</span>
                      {isSelected && <CheckIcon className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Center: Apartment Name */}
        <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none">
          <h1 className="text-[0.95rem] font-medium font-notosans tracking-tight leading-none truncate max-w-[90px] text-center" style={{ color: 'var(--mobile-text-primary)' }}>
            {apartmentLabel || 'Select Unit'}
          </h1>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-0">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg active:opacity-60 transition-colors"
              style={{ color: 'var(--mobile-text-muted)' }}
              aria-label="공간 선택"
            >
              <Building2Icon className="w-[1.2rem] h-[1.2rem]" />
            </button>
          )}
          {/* —— Theme Toggle Button —— */}
          {onThemeToggle && (
            <button
              onClick={onThemeToggle}
              className="theme-toggle-btn p-1.5 rounded-lg transition-all duration-300 active:scale-95"
              style={{ color: themePref === 'dark' ? '#6366f1' : themePref === 'light' ? '#f59e0b' : 'var(--mobile-text-muted)' }}
              aria-label="테마 전환"
              title="테마 전환"
            >
              <motion.div
                key={themePref}
                initial={{ scale: 0, rotate: -90, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                exit={{ scale: 0, rotate: 90, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                {themePref === 'dark' ? (
                  <MoonIcon className="w-[1.2rem] h-[1.2rem]" />
                ) : themePref === 'light' ? (
                  <SunIcon className="w-[1.2rem] h-[1.2rem]" />
                ) : (
                  <MonitorIcon className="w-[1.2rem] h-[1.2rem]" />
                )}
              </motion.div>
            </button>
          )}
          <button
            onClick={onOpenAlarms}
            className="relative p-1.5 rounded-lg active:opacity-60 transition-colors"
            style={{ color: 'var(--mobile-text-muted)' }}
            aria-label="알람"
          >
            <BellIcon className="w-[1.2rem] h-[1.2rem]" />
            {hasAlarms && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full shadow-[0_0_6px_rgba(239,68,68,0.8)]" />
            )}
          </button>
          <button
            onClick={onOpenMap}
            className="p-1.5 rounded-lg active:opacity-60 transition-colors"
            style={{ color: 'var(--mobile-text-muted)' }}
            aria-label="평면도"
          >
            <MapIcon className="w-[1.2rem] h-[1.2rem]" />
          </button>
        </div>
      </div>
    </div>
  );
}